import imgSrc from "./maps/RalphBalson.png";
import ParticlesRenderable from "./ParticlesRenderable";
import SimulationComputable from "./SimulationComputable";
import { requestWebGPU } from "~/src/utils";
import { convertToRange } from "~/src/utils/maths";
import TextureLoader from "~/src/utils/TextureLoader";
import WebGPUCompute from "~/src/WebGPUCompute";
import WebGPURenderer from "~/src/WebGPURenderer";
import Clock from "~/src/utils/Clock";

const ENTITIES_COUNT = 700;

const canvas = document.querySelector("canvas") as HTMLCanvasElement;
canvas.width = canvas.clientWidth * window.devicePixelRatio;
canvas.height = canvas.clientHeight * window.devicePixelRatio;

let renderer: WebGPURenderer;
let particlesRenderable: ParticlesRenderable;

let computer: WebGPUCompute;
let simulationComputable: SimulationComputable;

let device: GPUDevice;

let uniformBuffer;

const mouse = { x: 0, y: 0 };
const mouseTarget = { x: 0, y: 0 };

const clock = new Clock();

const createSharedUniformBuffersAndBindGroups: () => [
  GPUBindGroupLayout,
  GPUBindGroup,
  GPUBuffer
] = () => {
  const uniformBufferSize =
    1 * 2 * Float32Array.BYTES_PER_ELEMENT +
    1 * 2 * Float32Array.BYTES_PER_ELEMENT +
    1 * 2 * Float32Array.BYTES_PER_ELEMENT +
    1 * 1 * Float32Array.BYTES_PER_ELEMENT +
    3 * Float32Array.BYTES_PER_ELEMENT; // padding

  const uniformBuffer = device.createBuffer({
    size: uniformBufferSize,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });

  const sharedUniformsBindGroupLayout = device.createBindGroupLayout({
    entries: [
      {
        binding: 0,
        visibility:
          GPUShaderStage.VERTEX |
          GPUShaderStage.FRAGMENT |
          GPUShaderStage.COMPUTE,
        buffer: { type: "uniform" },
      },
    ],
  });

  const sharedUniformsBindGroup = device.createBindGroup({
    layout: sharedUniformsBindGroupLayout,
    entries: [
      {
        binding: 0,
        resource: {
          buffer: uniformBuffer,
        },
      },
    ],
  });

  return [
    sharedUniformsBindGroupLayout,
    sharedUniformsBindGroup,
    uniformBuffer,
  ];
};

const animate = () => {
  const { correction, delta } = clock.tick();
  mouse.x += (mouseTarget.x - mouse.x) * 0.05 * correction;
  mouse.y += (mouseTarget.y - mouse.y) * 0.05 * correction;

  const TILT = 5;

  canvas.style.transform = `rotateY(${convertToRange(
    mouse.x,
    [0, 1],
    [-TILT, TILT]
  )}deg) rotateX(${convertToRange(mouse.y, [0, 1], [TILT, -TILT])}deg)`;

  const uniformsArray = new Float32Array([
    canvas.width,
    canvas.height,
    2706 / 2000,
    1614 / 2000,
    mouse.x,
    mouse.y,
    delta,
  ]);
  device.queue.writeBuffer(uniformBuffer, 0, uniformsArray);

  for (let i = 0; i < 16; i++) {
    computer.compute();
    simulationComputable.swapBindGroups();
  }

  particlesRenderable.simulationSrcBindGroup =
    simulationComputable.getActiveBindGroup();
  renderer.render();

  requestAnimationFrame(animate);
};

const onMouseMove = ({ clientX, clientY }) => {
  mouseTarget.x = clientX / window.innerWidth;
  mouseTarget.y = clientY / window.innerHeight;
};

const init = async () => {
  device = (await requestWebGPU()) as GPUDevice;
  renderer = new WebGPURenderer(device, canvas);
  computer = new WebGPUCompute(device);
  const texture = await new TextureLoader(device).loadTextureFromImageSrc(
    imgSrc
  );

  const [sharedUniformsBindGroupLayout, sharedUniformsBindGroup, buffer] =
    createSharedUniformBuffersAndBindGroups();
  uniformBuffer = buffer;

  simulationComputable = new SimulationComputable(
    device,
    ENTITIES_COUNT,
    sharedUniformsBindGroupLayout,
    sharedUniformsBindGroup,
    2706 / 2000,
    1614 / 2000
  );
  computer.addComputable(simulationComputable);

  particlesRenderable = new ParticlesRenderable(
    device,
    renderer,
    ENTITIES_COUNT,
    sharedUniformsBindGroupLayout,
    sharedUniformsBindGroup,
    simulationComputable.getBindGroupLayout(),
    texture
  );
  renderer.addRenderable(particlesRenderable);

  clock.tick();
  requestAnimationFrame(animate);
  window.addEventListener("mousemove", onMouseMove);
};

init();
